import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useNavButton } from '../../hooks/useNavButton';
import SEOHead from '../../components/SEOHead';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'Sobre o EntreNós — Nossa Missão e Valores',
  description:
    'Conheça a história, missão e valores do EntreNós: a plataforma criada para fortalecer relacionamentos através de tecnologia e inteligência artificial especializada em casais.',
  url: `${SITE_URL}/sobre`,
  inLanguage: 'pt-BR',
  mainEntity: {
    '@type': 'Organization',
    name: 'EntreNós',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/1b0e966215e432d351b2e9c11ca0b10e/154dcc8a65fed03ee4761cc080c3eddf.png',
    },
    description:
      'EntreNós é uma plataforma digital para casais que combina inteligência artificial especializada em psicologia de relacionamentos com experiências interativas e personalizadas.',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'suporteentrenos@outlook.com',
      contactType: 'customer support',
    },
    foundingLocation: {
      '@type': 'Country',
      name: 'Brasil',
    },
  },
};

export default function AboutPage() {
  const [scrolled, setScrolled] = useState(false);
  const { navigateTo, isLoading } = useNavButton();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 40);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const values = [
    {
      icon: 'ri-hearts-line',
      gradient: 'from-rose-500 to-pink-500',
      title: 'Amor com Intenção',
      desc: 'Acreditamos que relacionamentos saudáveis não acontecem por acaso — eles são construídos com cuidado, atenção e esforço diário.',
    },
    {
      icon: 'ri-shield-check-line',
      gradient: 'from-pink-500 to-purple-500',
      title: 'Segurança e Privacidade',
      desc: 'Seus dados e os momentos íntimos do seu relacionamento são tratados com total sigilo e respeito. Nunca compartilhamos informações pessoais.',
    },
    {
      icon: 'ri-magic-line',
      gradient: 'from-purple-500 to-indigo-500',
      title: 'Inovação com Propósito',
      desc: 'Usamos inteligência artificial de ponta não por modismo, mas porque ela nos permite personalizar cada experiência de forma única para cada casal.',
    },
    {
      icon: 'ri-team-line',
      gradient: 'from-amber-500 to-rose-500',
      title: 'Inclusão e Diversidade',
      desc: 'O EntreNós é para todos os casais, independentemente de gênero, orientação sexual, idade ou tempo de relacionamento.',
    },
    {
      icon: 'ri-emotion-happy-line',
      gradient: 'from-teal-500 to-green-500',
      title: 'Bem-estar Emocional',
      desc: 'Priorizamos a saúde emocional dos casais. Nossas ferramentas são baseadas em psicologia e terapia relacional com evidências científicas.',
    },
    {
      icon: 'ri-lightbulb-line',
      gradient: 'from-rose-500 to-amber-500',
      title: 'Aprendizado Contínuo',
      desc: 'Evoluímos constantemente com base no feedback dos casais e nas mais recentes pesquisas sobre relacionamentos e bem-estar.',
    },
  ];

  const features = [
    { icon: '🎯', label: 'Missões personalizadas', desc: 'Desafios semanais personalizados' },
    { icon: '🤖', label: 'Layla — IA', desc: 'Consultora especializada 24h' },
    { icon: '🎮', label: 'Experiências', desc: 'Jogos, quizzes e desafios' },
    { icon: '🌟', label: 'Reconexão', desc: 'Plano de 7 dias estruturado' },
    { icon: '🏆', label: 'Conquistas', desc: 'Sistema de níveis e pontos' },
    { icon: '📸', label: 'Álbum', desc: 'Memórias do casal' },
  ];

  const team = [
    {
      name: 'Equipe de Produto',
      role: 'Design & Experiência',
      icon: 'ri-palette-line',
      color: 'from-rose-400 to-pink-500',
      desc: 'Responsável por criar interfaces intuitivas e experiências encantadoras para os casais.',
    },
    {
      name: 'Equipe de IA',
      role: 'Inteligência Artificial',
      icon: 'ri-robot-line',
      color: 'from-purple-400 to-indigo-500',
      desc: 'Desenvolve e treina os modelos de IA especializados em psicologia de casais e comunicação.',
    },
    {
      name: 'Equipe de Suporte',
      role: 'Atendimento & Cuidado',
      icon: 'ri-customer-service-2-line',
      color: 'from-amber-400 to-rose-500',
      desc: 'Garante que cada casal tenha a melhor experiência possível na plataforma.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50">
      <SEOHead
        title="Sobre o EntreNós — Nossa Missão, Valores e Equipe"
        description="Conheça a missão do EntreNós: fortalecer relacionamentos com tecnologia e IA especializada em casais. Saiba mais sobre nossos valores, a Layla e o que nos move."
        keywords="sobre EntreNós, missão EntreNós, plataforma casais, Layla IA, psicologia de casais, bem-estar emocional"
        canonical="/sobre"
        schemaJsonLd={aboutJsonLd}
        dateModified="2026-04-09"
      />
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/1b0e966215e432d351b2e9c11ca0b10e/154dcc8a65fed03ee4761cc080c3eddf.png"
                alt="EntreNós"
                fetchPriority="high"
                decoding="async"
                width="120"
                height="40"
                className="h-10 w-auto"
              />
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-gray-100 hover:shadow-md text-sm font-medium cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span className="whitespace-nowrap">Voltar ao início</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-28 pb-0 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-100/50 via-pink-100/50 to-purple-100/50" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-3xl mx-auto pb-16">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-rose-200 mb-6">
              <i className="ri-heart-fill text-rose-500"></i>
              <span className="text-sm font-medium text-gray-700">Nossa história e missão</span>
            </div>
            <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Sobre o{' '}
              <span className="bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500 bg-clip-text text-transparent">
                EntreNós
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Nascemos da crença de que todo relacionamento merece atenção, cuidado e ferramentas inteligentes para florescer. Somos uma plataforma criada para casais que querem investir no amor.
            </p>
            <button
              onClick={() => navigateTo('/cadastro', 'about-cta')}
              disabled={isLoading('about-cta')}
              className="inline-flex items-center gap-2 px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-rose-500 to-pink-500 rounded-full hover:from-rose-600 hover:to-pink-600 transition-all shadow-lg whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
            >
              {isLoading('about-cta') ? (
                <><i className="ri-loader-4-line animate-spin"></i> Aguarde...</>
              ) : (
                <><i className="ri-heart-line"></i> Começar Gratuitamente</>
              )}
            </button>
          </div>

        </div>
      </section>

      {/* Missão */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-flag-line"></i>
                Nossa Missão
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Fortalecer relacionamentos através da{' '}
                <span className="bg-gradient-to-r from-rose-500 to-pink-500 bg-clip-text text-transparent">
                  tecnologia e do amor
                </span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                O EntreNós nasceu de uma pergunta simples: <strong className="text-gray-800">por que não existe uma ferramenta realmente inteligente para ajudar casais a se conectarem melhor?</strong>
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Combinamos inteligência artificial especializada em psicologia de casais com experiências interativas e personalizadas para criar uma plataforma única — um espaço seguro onde dois parceiros podem crescer juntos, superar desafios e celebrar cada conquista do relacionamento.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Não somos um aplicativo de namoro. Não somos uma plataforma de terapia. Somos algo novo: <strong className="text-gray-800">um companheiro digital para casais que já se encontraram e querem continuar se escolhendo todos os dias.</strong>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-6 border border-rose-100">
                <div className="text-4xl font-bold bg-gradient-to-r from-rose-500 to-pink-500 bg-clip-text text-transparent mb-2">100%</div>
                <p className="text-sm font-semibold text-gray-800 mb-1">Focado em Casais</p>
                <p className="text-xs text-gray-500">Plataforma criada exclusivamente para fortalecer relacionamentos</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
                <div className="text-4xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent mb-2">IA</div>
                <p className="text-sm font-semibold text-gray-800 mb-1">Especializada</p>
                <p className="text-xs text-gray-500">Treinada em psicologia de casais e comunicação não-violenta</p>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-rose-50 rounded-2xl p-6 border border-amber-100">
                <div className="text-4xl font-bold bg-gradient-to-r from-amber-500 to-rose-500 bg-clip-text text-transparent mb-2">24h</div>
                <p className="text-sm font-semibold text-gray-800 mb-1">Disponível Sempre</p>
                <p className="text-xs text-gray-500">A Layla está disponível a qualquer hora para orientar o casal</p>
              </div>
              <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-2xl p-6 border border-teal-100">
                <div className="text-4xl font-bold bg-gradient-to-r from-teal-500 to-green-500 bg-clip-text text-transparent mb-2">🔒</div>
                <p className="text-sm font-semibold text-gray-800 mb-1">Privacidade Total</p>
                <p className="text-xs text-gray-500">Seus dados e momentos íntimos são protegidos com total sigilo</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* O que é o EntreNós */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-rose-200 mb-4">
              <i className="ri-apps-line text-rose-500"></i>
              <span className="text-sm font-medium text-gray-700">O que oferecemos</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Tudo que um casal precisa em um só lugar
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ferramentas inteligentes e experiências únicas para fortalecer o vínculo entre vocês dois
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {features.map((f) => (
              <div
                key={f.label}
                className="bg-white rounded-2xl p-6 shadow-sm border border-rose-100 hover:shadow-md hover:-translate-y-1 transition-all"
              >
                <div className="text-4xl mb-4">{f.icon}</div>
                <h3 className="font-bold text-gray-900 mb-1">{f.label}</h3>
                <p className="text-sm text-gray-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Layla */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-rose-500 via-pink-500 to-purple-500 rounded-3xl p-10 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-black/10 to-black/20"></div>
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <i className="ri-magic-fill"></i>
                  Conheça a Layla
                </div>
                <h2 className="text-4xl font-bold text-white mb-5">
                  A IA que entende de amor
                </h2>
                <p className="text-white/90 text-lg mb-6 leading-relaxed">
                  A Layla não é uma IA genérica. Ela foi treinada especificamente em psicologia de casais, comunicação não-violenta e terapia relacional para oferecer orientações realmente úteis.
                </p>
                <div className="space-y-3 mb-8">
                  {[
                    'Responde dúvidas sobre comunicação e conflitos',
                    'Cria experiências personalizadas para o casal',
                    'Oferece orientações baseadas em evidências científicas',
                    'Disponível 24 horas, 7 dias por semana',
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                      <span className="text-white/90 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-white/15 border border-white/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-white/80 text-lg flex-shrink-0 mt-0.5"></i>
                    <p className="text-white/80 text-sm">
                      A Layla é uma IA de apoio e <strong className="text-white">não substitui psicólogos ou terapeutas profissionais</strong>. Em situações complexas, recomendamos buscar ajuda especializada.
                    </p>
                  </div>
                </div>
              </div>

              {/* Card da Layla */}
              <div className="flex justify-center">
                <div className="relative w-full max-w-xs">
                  <div className="absolute -inset-4 bg-white/10 rounded-3xl blur-2xl"></div>
                  <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
                    <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-5 py-4 flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-white/50 flex-shrink-0">
                        <img
                          src="https://public.readdy.ai/ai/img_res/edited_3e7e25fdcc16c3a0dddd8ec5d539152a_fa4083a1.jpg"
                          alt="Layla"
                          loading="lazy"
                          decoding="async"
                          width="44"
                          height="44"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div>
                        <p className="text-white font-bold">Layla</p>
                        <p className="text-white/80 text-xs">IA Especialista em Relacionamentos</p>
                      </div>
                      <div className="ml-auto w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    </div>
                    <div className="p-5 space-y-3">
                      <div className="bg-gray-50 rounded-2xl rounded-tl-sm px-4 py-3">
                        <p className="text-sm text-gray-700">Olá! 💕 Como posso ajudar o relacionamento de vocês hoje?</p>
                      </div>
                      <div className="bg-rose-50 rounded-2xl rounded-tr-sm px-4 py-3 ml-6">
                        <p className="text-sm text-gray-700">Queremos nos reconectar mais...</p>
                      </div>
                      <div className="bg-gray-50 rounded-2xl rounded-tl-sm px-4 py-3">
                        <p className="text-sm text-gray-700">Ótimo! Tenho algumas sugestões especiais para vocês 🌟</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 via-rose-50 to-amber-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-rose-200 mb-4">
              <i className="ri-star-line text-rose-500"></i>
              <span className="text-sm font-medium text-gray-700">O que nos guia</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nossos Valores</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Cada decisão que tomamos é guiada por princípios que colocam os casais em primeiro lugar
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v) => (
              <div
                key={v.title}
                className="bg-white rounded-2xl p-7 shadow-sm border border-rose-100 hover:shadow-md transition-all"
              >
                <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br ${v.gradient} rounded-xl text-white mb-5`}>
                  <i className={`${v.icon} text-xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{v.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipe */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-team-line"></i>
              Quem somos
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">As pessoas por trás do EntreNós</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Uma equipe apaixonada por tecnologia e por relacionamentos saudáveis
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
            {team.map((t) => (
              <div key={t.name} className="text-center bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-8 border border-rose-100">
                <div className={`w-16 h-16 flex items-center justify-center bg-gradient-to-br ${t.color} rounded-2xl text-white text-2xl mx-auto mb-5`}>
                  <i className={t.icon}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{t.name}</h3>
                <p className="text-sm text-rose-600 font-medium mb-3">{t.role}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contato */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-rose-200 mb-6">
            <i className="ri-mail-line text-rose-500"></i>
            <span className="text-sm font-medium text-gray-700">Fale com a gente</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Entre em Contato</h2>
          <p className="text-lg text-gray-600 mb-10 max-w-xl mx-auto">
            Tem dúvidas, sugestões ou quer compartilhar a história do seu casal? Adoramos ouvir de vocês!
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
            <a
              href="mailto:suporteentrenos@outlook.com"
              className="bg-white rounded-2xl p-6 shadow-sm border border-rose-100 hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer block"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl text-white mx-auto mb-4">
                <i className="ri-mail-line text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Suporte</h3>
              <p className="text-sm text-rose-600">suporteentrenos@outlook.com</p>
            </a>
            <a
              href="mailto:suporteentrenos@outlook.com?subject=Nossa história no EntreNós"
              className="bg-white rounded-2xl p-6 shadow-sm border border-rose-100 hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer block"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl text-white mx-auto mb-4">
                <i className="ri-heart-line text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Compartilhe sua História</h3>
              <p className="text-sm text-rose-600">Inspire outros casais</p>
            </a>
            <a
              href="mailto:suporteentrenos@outlook.com?subject=Parceria com EntreNós"
              className="bg-white rounded-2xl p-6 shadow-sm border border-rose-100 hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer block"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-amber-500 to-rose-500 rounded-xl text-white mx-auto mb-4">
                <i className="ri-briefcase-line text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Parcerias</h3>
              <p className="text-sm text-rose-600">Vamos conversar</p>
            </a>
          </div>

          <button
            onClick={() => navigateTo('/cadastro', 'about-bottom-cta')}
            disabled={isLoading('about-bottom-cta')}
            className="inline-flex items-center gap-2 px-10 py-4 text-base font-bold text-white bg-gradient-to-r from-rose-500 to-pink-500 rounded-full hover:from-rose-600 hover:to-pink-600 transition-all shadow-lg whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            {isLoading('about-bottom-cta') ? (
              <><i className="ri-loader-4-line animate-spin"></i> Aguarde...</>
            ) : (
              <><i className="ri-heart-fill"></i> Começar Gratuitamente</>
            )}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-rose-950 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-sm text-rose-300">
            <Link to="/" className="hover:text-white transition-colors">Início</Link>
            <span>·</span>
            <Link to="/privacidade" className="hover:text-white transition-colors">Privacidade</Link>
            <span>·</span>
            <Link to="/termos" className="hover:text-white transition-colors">Termos de Uso</Link>
            <span>·</span>
            <Link to="/sobre" className="text-white font-medium">Sobre</Link>
          </div>
          <p className="text-rose-300 text-sm">© 2025 EntreNós. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
